import {
  AnyDashboardArtifactSpec,
  DashboardArtifactCell,
  DashboardColumnType,
  DashboardNumberFormat,
  type DashboardAdHocDataset,
  type DashboardArtifactSpecV2,
  type DashboardDatasetColumn,
  type DashboardWidgetV2,
  type ResolvedDashboardWidget,
} from "@t3tools/contracts";
import { upgradeSpecV1toV2 } from "@t3tools/shared/dashboardSpecUpgrade";
import { Option, Schema } from "effect";

/** Wire shapes of `/api/v1/dashboards/artifacts`. */
export const DashboardArtifactStatus = Schema.Literals(["draft", "published", "archived"]);
export type DashboardArtifactStatus = typeof DashboardArtifactStatus.Type;

export const DashboardArtifactSummary = Schema.Struct({
  id: Schema.String,
  slug: Schema.String,
  title: Schema.String,
  status: DashboardArtifactStatus,
  ownerEmail: Schema.String,
  sourceSessionId: Schema.NullOr(Schema.String),
  latestVersion: Schema.NullOr(Schema.Number),
  publishedVersion: Schema.NullOr(Schema.Number),
  createdAt: Schema.String,
  updatedAt: Schema.String,
});
export type DashboardArtifactSummary = typeof DashboardArtifactSummary.Type;

export const DashboardArtifactVersionSummary = Schema.Struct({
  id: Schema.String,
  version: Schema.Number,
  contentHash: Schema.String,
  createdByEmail: Schema.String,
  sourceSessionId: Schema.NullOr(Schema.String),
  sourceTurnId: Schema.NullOr(Schema.String),
  createdAt: Schema.String,
  published: Schema.Boolean,
});
export type DashboardArtifactVersionSummary = typeof DashboardArtifactVersionSummary.Type;

const DashboardArtifactDetailResponse = Schema.Struct({
  artifact: DashboardArtifactSummary,
  version: Schema.Struct({ ...DashboardArtifactVersionSummary.fields, spec: Schema.Unknown }),
});
const DashboardArtifactListResponse = Schema.Struct({
  artifacts: Schema.Array(DashboardArtifactSummary),
});
const DashboardArtifactVersionsResponse = Schema.Struct({
  versions: Schema.Array(DashboardArtifactVersionSummary),
});
const DashboardArtifactChangeResponse = Schema.Struct({ artifact: DashboardArtifactSummary });
/** The version an edit saved, and the artifact it now belongs to. */
const DashboardArtifactEditResponse = Schema.Struct({
  artifact: DashboardArtifactSummary,
  version: DashboardArtifactVersionSummary,
});
export type DashboardArtifactEditResult = typeof DashboardArtifactEditResponse.Type;

export type ArtifactCell = DashboardArtifactCell;
export type ArtifactRow = Readonly<Record<string, ArtifactCell>>;

/**
 * `POST :ref/data` rows for one dataset. Only parameter options still read
 * it: widgets load through the resolve stream.
 */
export const DashboardArtifactOptionsResponse = Schema.Struct({
  columns: Schema.Array(
    Schema.Struct({
      name: Schema.String,
      type: DashboardColumnType,
      format: Schema.optionalKey(DashboardNumberFormat),
      label: Schema.optionalKey(Schema.String),
    }),
  ),
  rows: Schema.Array(Schema.Record(Schema.String, DashboardArtifactCell)),
});
export type DashboardArtifactOptionsResponse = typeof DashboardArtifactOptionsResponse.Type;
export type ArtifactColumn = DashboardDatasetColumn;

/** Every stored version renders as spec v2; v1 specs are upgraded on decode. */
export type ArtifactSpec = DashboardArtifactSpecV2;
export type ArtifactWidget = DashboardWidgetV2;
export type ArtifactDataWidget = Exclude<DashboardWidgetV2, { kind: "text" }>;
export type ArtifactDataset = DashboardAdHocDataset;

/** A loaded artifact version with its spec decoded against the contract. */
export type DashboardArtifactDocument = {
  artifact: DashboardArtifactSummary;
  version: DashboardArtifactVersionSummary;
  spec: ArtifactSpec;
};

export class DashboardArtifactSpecError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "DashboardArtifactSpecError";
  }
}

/** Decodes any stored spec version and upgrades it to v2. */
export function decodeDashboardArtifactSpec(spec: unknown): ArtifactSpec {
  try {
    return upgradeSpecV1toV2(Schema.decodeUnknownSync(AnyDashboardArtifactSpec)(spec));
  } catch (error) {
    throw new DashboardArtifactSpecError(
      `This dashboard spec is invalid: ${error instanceof Error ? error.message : String(error)}`,
    );
  }
}

export function decodeDashboardArtifactDetail(body: unknown): DashboardArtifactDocument {
  const decoded = Schema.decodeUnknownOption(DashboardArtifactDetailResponse)(body);
  if (Option.isNone(decoded)) throw new Error("Dashboard artifact response was malformed");
  const { spec, ...version } = decoded.value.version;
  return { artifact: decoded.value.artifact, version, spec: decodeDashboardArtifactSpec(spec) };
}

export function decodeDashboardArtifactList(
  body: unknown,
): ReadonlyArray<DashboardArtifactSummary> {
  const decoded = Schema.decodeUnknownOption(DashboardArtifactListResponse)(body);
  if (Option.isNone(decoded)) throw new Error("Dashboard list response was malformed");
  return decoded.value.artifacts;
}

export function decodeDashboardArtifactVersions(
  body: unknown,
): ReadonlyArray<DashboardArtifactVersionSummary> {
  const decoded = Schema.decodeUnknownOption(DashboardArtifactVersionsResponse)(body);
  if (Option.isNone(decoded)) throw new Error("Dashboard versions response was malformed");
  return decoded.value.versions;
}

export function decodeDashboardArtifactChange(body: unknown): DashboardArtifactSummary {
  const decoded = Schema.decodeUnknownOption(DashboardArtifactChangeResponse)(body);
  if (Option.isNone(decoded)) throw new Error("Dashboard response was malformed");
  return decoded.value.artifact;
}

export function decodeDashboardArtifactEditResult(body: unknown): DashboardArtifactEditResult {
  const decoded = Schema.decodeUnknownOption(DashboardArtifactEditResponse)(body);
  if (Option.isNone(decoded)) throw new Error("Dashboard edit response was malformed");
  return decoded.value;
}

export function decodeDashboardArtifactOptions(body: unknown): DashboardArtifactOptionsResponse {
  const decoded = Schema.decodeUnknownOption(DashboardArtifactOptionsResponse)(body);
  if (Option.isNone(decoded)) throw new Error("Dashboard options response was malformed");
  return decoded.value;
}

export type ResolvedWidgetOf<K extends ArtifactWidget["kind"]> = Extract<
  ResolvedDashboardWidget,
  { kind: K }
>;
